import React, { useEffect, useState } from 'react';
import { ArrowLeft, Clock, Calendar, BookOpen, Share2, CheckCircle2, ArrowRight } from 'lucide-react';
import { AnimatedGridPattern } from '../components/ui/AnimatedGridPattern';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { fetchBlogArticleBySlug, fetchBlogArticles, formatDate, BlogArticle } from '../lib/contentful';
interface BlogArticlePageProps {
  slug: string;
  onNavigate: (page: string, data?: any) => void;
}
export function BlogArticlePage({
  slug,
  onNavigate
}: BlogArticlePageProps) {
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    async function loadArticle() {
      try {
        setLoading(true);
        const [articleData, allArticles] = await Promise.all([fetchBlogArticleBySlug(slug), fetchBlogArticles()]);
        if (!articleData) {
          setError('Artikel nicht gefunden');
          return;
        }
        setArticle(articleData);
        // Get related articles (same category, excluding current)
        const related = allArticles.filter(a => a.category === articleData.category && a.id !== articleData.id).slice(0, 2);
        setRelatedArticles(related);
      } catch (err) {
        setError('Artikel konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    }
    loadArticle();
  }, [slug]);
  // Loading State
  if (loading) {
    return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center" data-id="element-484">
        <div className="text-center" data-id="element-485">
          <div className="h-12 w-12 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin mb-4 mx-auto" data-id="element-486"></div>
          <p className="text-slate-500 dark:text-slate-400" data-id="element-487">
            Artikel wird geladen...
          </p>
        </div>
      </div>;
  }
  // Error State
  if (error || !article) {
    return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center" data-id="element-488">
        <div className="text-center max-w-md mx-auto px-4" data-id="element-489">
          <div className="inline-flex h-16 w-16 bg-slate-100 dark:bg-slate-800 rounded-2xl items-center justify-center mb-6" data-id="element-490">
            <BookOpen className="h-8 w-8 text-slate-400" data-id="element-491" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-492">
            Artikel nicht gefunden
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6" data-id="element-493">
            Der gesuchte Artikel existiert nicht oder wurde verschoben.
          </p>
          <Button onClick={() => onNavigate('blog')} variant="outline" data-id="element-494">
            <ArrowLeft className="mr-2 h-4 w-4" data-id="element-495" />
            Zurück zum Ratgeber
          </Button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300" data-id="element-496">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-sky-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/20 pt-24 pb-12 lg:pt-32 lg:pb-16 border-b border-slate-200 dark:border-slate-800" data-id="element-497">
        <AnimatedGridPattern numSquares={20} maxOpacity={0.1} duration={3} repeatDelay={1} className="[mask-image:radial-gradient(500px_circle_at_center,white,transparent)] inset-x-0 inset-y-[-30%] h-[200%] skew-y-12" data-id="element-498" />

        <div className="container mx-auto px-4 relative z-10 max-w-4xl" data-id="element-499">
          {/* Breadcrumb */}
          <button onClick={() => onNavigate('blog')} className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-6" data-id="element-500">
            <ArrowLeft className="h-4 w-4" data-id="element-501" />
            Zurück zum Ratgeber
          </button>

          {/* Category Badge */}
          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800 mb-4" data-id="element-502">
            {article.category}
          </Badge>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight leading-tight" data-id="element-503">
            {article.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400" data-id="element-504">
            <span className="flex items-center gap-1.5" data-id="element-505">
              <Clock className="h-4 w-4" data-id="element-506" />
              {article.readTime} Lesezeit
            </span>
            <span className="flex items-center gap-1.5" data-id="element-507">
              <Calendar className="h-4 w-4" data-id="element-508" />
              {formatDate(article.publishedDate)}
            </span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="container mx-auto px-4 py-12 lg:py-16" data-id="element-509">
        <div className="max-w-4xl mx-auto" data-id="element-510">
          <div className="grid lg:grid-cols-4 gap-10" data-id="element-511">
            {/* Main Content */}
            <div className="lg:col-span-3" data-id="element-512">
              {/* Excerpt */}
              <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed mb-8 font-medium" data-id="element-513">
                {article.excerpt}
              </p>

              {/* Article Body */}
              <div className="prose prose-slate dark:prose-invert prose-lg max-w-none
                  prose-headings:font-bold prose-headings:text-slate-900 dark:prose-headings:text-white
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed
                  prose-ul:text-slate-600 dark:prose-ul:text-slate-300
                  prose-li:marker:text-blue-500
                  prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline" dangerouslySetInnerHTML={{
              __html: article.content
            }} data-id="element-514" />

              {/* CTA Box */}
              <div className="mt-12 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-2xl p-6 md:p-8 border border-blue-100 dark:border-blue-900/50" data-id="element-515">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-516">
                  Fragen zu diesem Thema?
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-5" data-id="element-517">
                  Unsere Experten helfen dir gerne weiter – persönlich und
                  unabhängig.
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-518">
                  <Button onClick={() => onNavigate('beratung')} variant="premium" data-id="element-519">
                    Beratung anfragen
                    <ArrowRight className="ml-2 h-4 w-4" data-id="element-520" />
                  </Button>
                  <Button onClick={() => onNavigate('rechner')} variant="outline" data-id="element-521">
                    Zum Vergleichsrechner
                  </Button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1" data-id="element-522">
              <div className="sticky top-24 space-y-6" data-id="element-523">
                {/* Quick Info */}
                <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm" data-id="element-524">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-sm uppercase tracking-wide" data-id="element-525">
                    Auf einen Blick
                  </h4>
                  <ul className="space-y-3 text-sm" data-id="element-526">
                    <li className="flex items-start gap-2.5" data-id="element-527">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-528" />
                      <span className="text-slate-600 dark:text-slate-400" data-id="element-529">
                        Verständlich erklärt
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5" data-id="element-530">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-531" />
                      <span className="text-slate-600 dark:text-slate-400" data-id="element-532">
                        Praxisnahe Tipps
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5" data-id="element-533">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-534" />
                      <span className="text-slate-600 dark:text-slate-400" data-id="element-535">
                        Unabhängige Einschätzung
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-slate-200 dark:border-slate-800 shadow-sm" data-id="element-536">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-4 text-sm uppercase tracking-wide" data-id="element-537">
                      Ähnliche Artikel
                    </h4>
                    <div className="space-y-4" data-id="element-538">
                      {relatedArticles.map(related => <button key={related.id} onClick={() => onNavigate('blog-article', {
                    slug: related.slug
                  })} className="block w-full text-left group" data-id="element-539">
                          <h5 className="text-sm font-medium text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2 mb-1" data-id="element-540">
                            {related.title}
                          </h5>
                          <span className="text-xs text-slate-500 dark:text-slate-400" data-id="element-541">
                            {related.readTime}
                          </span>
                        </button>)}
                    </div>
                  </div>}

                {/* Back to Blog */}
                <Button onClick={() => onNavigate('blog')} variant="ghost" className="w-full justify-center" data-id="element-542">
                  <ArrowLeft className="mr-2 h-4 w-4" data-id="element-543" />
                  Alle Artikel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}